import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { CLIENT_LOGOS, EMBASSIES, EXPERIENCE_STATS } from "@/data/content";

const Experience = () => (
    <div data-testid="experience-page">
        <Seo
            title="Experience & Associations | Kavraj Spaces — New Delhi"
            description="Experience across aviation, hospitality, finance, education, government and diplomacy — including GMR, Airports Authority of India, The Lalit Hotel, Domino's and Delhi University."
        />

        <PageHero
            eyebrow="Experience"
            title="A record across sectors and scales."
            intro="The businesses behind Kavraj Spaces have worked across hospitality, finance, education, healthcare and government — experience now brought together under one coordinated standard."
            testid="experience-hero"
        />

        <section className="bg-cream" data-testid="experience-associations">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <Reveal>
                    <h2 className="max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Experience and associations include
                    </h2>
                </Reveal>
                <div className="mt-16 grid grid-cols-2 items-center justify-items-center gap-x-8 gap-y-12 sm:grid-cols-3 lg:grid-cols-5">
                    {CLIENT_LOGOS.map((logo, i) => (
                        <Reveal key={logo.id} delay={(i % 5) * 80}>
                            <div className="flex flex-col items-center gap-3">
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    loading="lazy"
                                    data-testid={`client-logo-${logo.id}`}
                                    className={`w-auto object-contain ${logo.className || "h-14 sm:h-16"}`}
                                />
                                {logo.caption && (
                                    <p
                                        data-testid={`client-logo-caption-${logo.id}`}
                                        className="text-center text-xs font-semibold uppercase tracking-[0.15em] text-stone2"
                                    >
                                        {logo.caption}
                                    </p>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={100}>
                    <p className="mt-16 max-w-3xl text-sm leading-relaxed text-stone2" data-testid="experience-disclaimer">
                        Logos and names shown reflect the past professional experience and
                        associations of the founding businesses. They are shared as a record of work
                        undertaken and do not imply current engagement or endorsement by the
                        organisations mentioned.
                    </p>
                </Reveal>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="experience-diplomatic">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-28">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        Diplomatic leasing
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Trusted with the requirements of embassies and consulates.
                    </h2>
                </Reveal>
                <Reveal delay={120}>
                    <p className="text-base leading-relaxed text-stone2">
                        Our real estate practice has supported leasing requirements for multiple
                        embassies and consulates in New Delhi — including those of the{" "}
                        {EMBASSIES.slice(0, -1).join(", ")} and {EMBASSIES[EMBASSIES.length - 1]} —
                        work that calls for discretion, protocol awareness and patience.
                    </p>
                    <p className="mt-5 text-sm italic leading-relaxed text-stone2/80">
                        This reflects experience supporting their requirements; it does not imply
                        endorsement by any mission or government.
                    </p>
                </Reveal>
            </div>
        </section>

        <section className="bg-charcoal" data-testid="experience-stats">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
                <div className="grid grid-cols-1 gap-12 sm:grid-cols-3">
                    {EXPERIENCE_STATS.map((stat, i) => (
                        <Reveal key={stat.label} delay={i * 120}>
                            <div className="border-l-2 border-bronze pl-6" data-testid={`experience-stat-${i}`}>
                                <p className="font-serif text-5xl tracking-tight text-cream lg:text-6xl">
                                    {stat.value}
                                </p>
                                <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-cream/70">
                                    {stat.label}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <CtaBand
            title="Bring this experience to your requirement."
            text="Whatever the sector, the first step is a straightforward conversation."
        />
    </div>
);

export default Experience;
